import type { OcrFields, OcrLine, FieldConfidence } from "../../types/scan";

/** Below this merged confidence (0–100), the user should verify the field. */
export const LOW_FIELD_CONFIDENCE = 70;

export function needsFieldVerification(
  conf: number | undefined,
  hasValue: boolean
): boolean {
  return hasValue && (conf == null || conf < LOW_FIELD_CONFIDENCE);
}

const OCR_TIMEOUT_MS = 5000;
const OCR_UPSCALE_FACTOR = 2;
const OCR_WORKER_POOL_MAX = 10;

/**
 * Upscale image 2× with high-quality smoothing. Tesseract was trained on ~300 DPI text;
 * phone photos of a card are often 100–150 DPI, so upscaling significantly helps recognition.
 */
function upscaleForOcr(dataUri: string, factor: number = OCR_UPSCALE_FACTOR): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const out = document.createElement("canvas");
      out.width = w * factor;
      out.height = h * factor;
      const ctx = out.getContext("2d");
      if (!ctx) {
        resolve(dataUri);
        return;
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(img, 0, 0, w, h, 0, 0, out.width, out.height);
      try {
        resolve(out.toDataURL("image/jpeg", 0.95));
      } catch {
        resolve(dataUri);
      }
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

export interface OcrResult {
  fields: OcrFields;
  rawText: string;
  /** Line-level output from Tesseract (text, confidence, bbox) when blocks output is used. */
  lines?: OcrLine[];
  /** True when OCR hit the timeout (user should be prompted to enter manually). */
  timedOut?: boolean;
}

const OCR_LOG_PREFIX = "[Scan OCR]";
const toSecs = (ms: number): string => `${(ms / 1000).toFixed(2)}s`;

/** French letters for names on BTP cards (whitelist must include these or accents are dropped). */
const OCR_CHAR_WHITELIST =
  "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz" +
  "ÀÂÄÉÈÊËÏÎÔÙÛÜŸÇàâäéèêëïîôùûüÿç" +
  "0123456789, -'.°:";

type TesseractWorker = Awaited<ReturnType<Awaited<typeof import("tesseract.js")>["createWorker"]>>;

const workerPool: TesseractWorker[] = [];
let workerPoolPromise: Promise<typeof import("tesseract.js")> | null = null;
/** Bump when worker params change so pooled workers are recreated. */
const OCR_WORKER_CONFIG_VERSION = 2;
let workerPoolConfigVersion = 0;

function getTesseract(): Promise<typeof import("tesseract.js")> {
  if (!workerPoolPromise) workerPoolPromise = import("tesseract.js");
  return workerPoolPromise;
}

async function getWorkers(count: number): Promise<{ workers: TesseractWorker[]; reused: boolean }> {
  if (workerPoolConfigVersion !== OCR_WORKER_CONFIG_VERSION) {
    await Promise.all(workerPool.map((w) => w.terminate().catch(() => undefined)));
    workerPool.length = 0;
    workerPoolConfigVersion = OCR_WORKER_CONFIG_VERSION;
  }
  const { createWorker, PSM } = await getTesseract();
  const reused = workerPool.length >= count;
  const needed = Math.max(0, count - workerPool.length);
  if (needed > 0) {
    const toCreate = Math.min(needed, OCR_WORKER_POOL_MAX - workerPool.length);
    const psmBlock = (PSM?.SINGLE_BLOCK ?? 6) as import("tesseract.js").PSM;
    const created = await Promise.all(
      Array.from({ length: toCreate }, async () => {
        const worker = await createWorker("fra", 1);
        await worker.setParameters({
          tessedit_pageseg_mode: psmBlock,
          tessedit_char_whitelist: OCR_CHAR_WHITELIST,
          load_system_dawg: "0",
          load_freq_dawg: "0",
          language_model_penalty_non_dict_word: "0",
          language_model_penalty_non_freq_dict_word: "0",
        });
        return worker;
      })
    );
    workerPool.push(...created);
  }
  const workers = workerPool.splice(0, count);
  return { workers, reused };
}

function releaseWorkers(workers: TesseractWorker[]): void {
  workerPool.push(...workers);
}

/**
 * Run OCR on one image using an existing worker (no terminate). Used by runOcrBatch.
 */
async function runOcrWithWorker(
  worker: TesseractWorker,
  dataUri: string,
  frameLabel?: string
): Promise<OcrResult> {
  const runLabel = frameLabel ?? "frame";
  const runStart = performance.now();
  const t0 = performance.now();
  const upscaled = await upscaleForOcr(dataUri);
  console.log(`${OCR_LOG_PREFIX} ${runLabel} upscale took ${toSecs(performance.now() - t0)}`);

  const timeoutPromise = new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error("OCR_TIMEOUT")), OCR_TIMEOUT_MS)
  );
  const t1 = performance.now();
  try {
    const result = await Promise.race([
      worker.recognize(upscaled),
      timeoutPromise
    ]);
    console.log(`${OCR_LOG_PREFIX} ${runLabel} recognize took ${toSecs(performance.now() - t1)}`);
    const data = result?.data as unknown as Record<string, unknown> | undefined;
    if (!data) return { fields: {} as OcrFields, rawText: "" };

    const lines = extractLinesFromRecognizeData(data);
    if (lines.length > 0) {
      // Do not filter empty lines: index must match physical line (line 4 = index 3 = entreprise)
      const rawText = lines.map((l) => l.text.trim()).join("\n");
      console.log(
        `${OCR_LOG_PREFIX} ${runLabel} lines JSON:`,
        JSON.stringify(
          {
            frame: runLabel,
            lines: lines.map((line, index) => ({
              index,
              text: line.text.trim(),
              confidence: line.confidence,
              bbox: line.bbox
            }))
          },
          null,
          2
        )
      );
      return {
        fields: parseOcrTextFromRaw(rawText),
        rawText,
        lines
      };
    }
    const text = typeof data.text === "string" ? data.text : "";
    if (text) {
      const rawText = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter(Boolean)
        .join("\n");
      return { fields: parseOcrTextFromRaw(text), rawText };
    }
  } catch (e) {
    const timedOut = e instanceof Error && e.message === "OCR_TIMEOUT";
    if (timedOut) {
      console.warn(`${OCR_LOG_PREFIX} ${runLabel} timeout after ${toSecs(OCR_TIMEOUT_MS)}`);
    }
    return { fields: {} as OcrFields, rawText: "", timedOut: timedOut || undefined };
  } finally {
    console.log(`${OCR_LOG_PREFIX} ${runLabel} analysis took ${toSecs(performance.now() - runStart)}`);
  }
  return { fields: {} as OcrFields, rawText: "" };
}

/**
 * Extract line array from Tesseract recognize result (data.lines or data.blocks[].lines).
 */
function normBbox(b: Record<string, number> | undefined): OcrLine["bbox"] {
  if (!b || typeof b !== "object") return { x0: 0, y0: 0, x1: 0, y1: 0 };
  return {
    x0: typeof b.x0 === "number" ? b.x0 : typeof b.left === "number" ? b.left : 0,
    y0: typeof b.y0 === "number" ? b.y0 : typeof b.top === "number" ? b.top : 0,
    x1: typeof b.x1 === "number" ? b.x1 : typeof b.right === "number" ? b.right : 0,
    y1: typeof b.y1 === "number" ? b.y1 : typeof b.bottom === "number" ? b.bottom : 0
  };
}

function extractLinesFromRecognizeData(data: Record<string, unknown>): OcrLine[] {
  const out: OcrLine[] = [];
  const pushLine = (line: { text?: string; confidence?: number; bbox?: Record<string, number> }) => {
    out.push({
      text: line?.text ?? "",
      confidence: typeof line?.confidence === "number" ? line.confidence : 0,
      bbox: normBbox(line?.bbox)
    });
  };
  if (Array.isArray(data.lines)) {
    for (const line of data.lines as Array<{ text?: string; confidence?: number; bbox?: Record<string, number> }>) {
      pushLine(line);
    }
    return out;
  }
  if (Array.isArray(data.blocks)) {
    for (const block of data.blocks as Array<{ lines?: Array<{ text?: string; confidence?: number; bbox?: Record<string, number> }> }>) {
      if (!Array.isArray(block?.lines)) continue;
      for (const line of block.lines) pushLine(line);
    }
  }
  return out;
}

/**
 * Run OCR on multiple images in parallel using a pool of workers. Workers are reused across
 * calls, so the first run pays worker load once; subsequent runs skip it (~4s saved).
 */
export async function runOcrBatch(dataUris: string[]): Promise<OcrResult[]> {
  if (dataUris.length === 0) return [];

  const count = Math.min(dataUris.length, OCR_WORKER_POOL_MAX);
  console.log(`${OCR_LOG_PREFIX} analyzing ${count} frame(s)`);
  const t0 = performance.now();
  const { workers, reused } = await getWorkers(count);
  const workerLoadMs = performance.now() - t0;
  console.log(
    `${OCR_LOG_PREFIX} worker setup took ${toSecs(workerLoadMs)} ${reused ? "(reused)" : "(created)"}`
  );

  try {
    const batchOcrStart = performance.now();
    const results = await Promise.all(
      dataUris.slice(0, count).map((uri, i) => runOcrWithWorker(workers[i], uri, `frame ${i + 1}`))
    );
    console.log(
      `${OCR_LOG_PREFIX} batch lines JSON:`,
      JSON.stringify(
        {
          frames: results.map((result, frameIndex) => ({
            frame: `frame ${frameIndex + 1}`,
            lines: (result.lines ?? []).map((line, lineIndex) => ({
              index: lineIndex,
              text: line.text.trim(),
              confidence: line.confidence,
              bbox: line.bbox
            }))
          }))
        },
        null,
        2
      )
    );
    console.log(`${OCR_LOG_PREFIX} ${count} frames analysis took ${toSecs(performance.now() - batchOcrStart)}`);
    return results;
  } finally {
    releaseWorkers(workers);
  }
}

/**
 * Run Tesseract OCR on a single image (lazy-loaded). Uses pool when possible.
 * Prefer runOcrBatch when you have multiple images to avoid creating extra workers.
 */
export async function runOcr(dataUri: string): Promise<OcrResult> {
  const [result] = await runOcrBatch([dataUri]);
  return result ?? { fields: {} as OcrFields, rawText: "" };
}

function stripLeadingNoise(text: string): string {
  return text
    .replace(/^[\s\-\.,;:°'"!?\|\/\\*#@~`^]+/, "")
    .replace(/^(\b[a-zA-Z]\b\s*)+/, "")
    .replace(/^[\s\-\.,;:°'"]+/, "")
    .trim();
}

function cleanName(text: string): string {
  return stripLeadingNoise(text)
    .replace(/^[^a-zA-ZÀ-ÿ]+/, "")
    .replace(/[,\.]$/, "")
    .trim();
}

function isForbiddenFieldLine(text: string): boolean {
  const normalized = text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  if (!normalized) return false;
  // Never use expiration/date label lines as extracted identity fields.
  if (/date\s*d[' ]?expiration/.test(normalized)) return true;
  if (/date\s*d[' ]?exp/.test(normalized)) return true;
  if (/expiration/.test(normalized)) return true;
  return false;
}

function normalizeLineForMatch(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Detect the "Sexe" label line when OCR splits or garbles it (not card-specific typos).
 */
export function isSexeLine(text: string): boolean {
  const n = normalizeLineForMatch(text);
  if (!n || n.length > 40) return false;
  if (/\bsexe?\b/.test(n)) return true;
  if (/\bsex\b/.test(n)) return true;
  if (/sexe?\s*[:°]/.test(n)) return true;
  if (/xe\s*[:°]/.test(n)) return true;
  if (/\bsexe?\s+[mf]\b/.test(n)) return true;
  if (/\bsex\s+[mf]\b/.test(n)) return true;
  // Short lines only: fragment "exe" from a broken "Sexe"
  if (n.length <= 12 && /\bexe\b/.test(n)) return true;
  if (/^s\s*e\s*x/.test(n)) return true;
  return false;
}

/** Gender field misread as "Sou 4" / "Seu 4" — exclude from name/company, not used as Sexe anchor. */
function isLikelyGenderValueLine(text: string): boolean {
  if (isSexeLine(text)) return true;
  const cleaned = cleanName(text);
  return /^[a-zA-ZÀ-ÿ]{2,5}\s*\d{1,2}$/.test(cleaned);
}

function findSexeIndex(lines: Array<{ index: number; text: string }>): number {
  for (const line of lines) {
    if (isSexeLine(line.text)) return line.index;
  }
  return -1;
}

function findCarteIndex(lines: Array<{ index: number; text: string }>): number {
  for (const line of lines) {
    if (/carte\s*n[°o°]/i.test(line.text) || /carte\s*n\s*:/i.test(line.text)) return line.index;
  }
  return -1;
}

/** Matches "Carte n°:", "Carte n:", "Carte n° :", etc. */
const CARTE_LABEL_RE = /carte\s*n[°o°]?\s*:/i;

/** How many characters after the label get letter→digit OCR correction. */
const CARD_NUMBER_LETTER_MAP_LEN = 10;

/** How far after the label we scan for digit runs (spaces + trailing groups). */
const CARD_NUMBER_SCAN_LEN = 36;

/**
 * Tesseract often confuses these glyphs with digits on BTP cards.
 * Applied only in the first {@link CARD_NUMBER_LETTER_MAP_LEN} chars after "Carte n° :".
 */
export const OCR_LETTER_LIKE_DIGIT: Readonly<Record<string, string>> = {
  O: "0",
  o: "0",
  Q: "0",
  D: "0",
  I: "1",
  l: "1",
  L: "1",
  "|": "1",
  Z: "2",
  z: "2",
  S: "5",
  s: "5",
  G: "6",
  b: "6",
  T: "7",
  t: "7",
  B: "8",
  R: "8",
  g: "9",
};

/**
 * Replace OCR letters with their most likely digit, up to `maxLen` characters.
 */
export function mapOcrLettersToDigits(
  text: string,
  likeness: Readonly<Record<string, string>> = OCR_LETTER_LIKE_DIGIT,
  maxLen: number = text.length
): string {
  const limit = Math.min(text.length, maxLen);
  let out = "";
  for (let i = 0; i < limit; i++) {
    const ch = text[i];
    out += likeness[ch] ?? ch;
  }
  return out + text.slice(limit);
}

function sliceAfterCarteLabel(text: string): string | null {
  const match = text.match(CARTE_LABEL_RE);
  if (!match || match.index == null) return null;
  return text.slice(match.index + match[0].length);
}

/**
 * Extract 10-digit card id from text after "Carte n° :" only (ignores leading line noise like "m5").
 */
function extractDigitsAfterCarteLabel(text: string): string | null {
  const segment = sliceAfterCarteLabel(text);
  if (!segment) return null;

  const head = segment.slice(0, CARD_NUMBER_LETTER_MAP_LEN);
  const tail = segment.slice(CARD_NUMBER_LETTER_MAP_LEN, CARD_NUMBER_SCAN_LEN);
  const normalized = mapOcrLettersToDigits(head) + tail;
  const digits = normalized.replace(/[^0-9]/g, "");
  if (digits.length < 9) return null;
  return digits.slice(0, 10).padStart(10, "0");
}

function extractCardNumberFromText(text: string): string | null {
  const fromLabel = extractDigitsAfterCarteLabel(text);
  if (fromLabel) return fromLabel;

  // Lines that mention carte but with a non-standard label spelling
  const carteIdx = text.search(/carte/i);
  if (carteIdx >= 0) {
    const afterCarte = text.slice(carteIdx);
    const fromAfterCarte = extractDigitsAfterCarteLabel(afterCarte);
    if (fromAfterCarte) return fromAfterCarte;
  }

  return null;
}

function isAllCapsText(text: string): boolean {
  const letters = text.replace(/[^a-zA-Z]/g, "");
  if (letters.length === 0) return false;
  return letters === letters.toUpperCase();
}

function isCompanyLike(text: string): boolean {
  if (isForbiddenFieldLine(text)) return false;
  if (isLikelyGenderValueLine(text)) return false;
  const cleaned = cleanName(text);
  if (cleaned.length < 4) return false;
  if (/^\d[\d\s]+$/.test(cleaned)) return false;
  if (/^[^a-zA-ZÀ-ÿ]+$/.test(cleaned)) return false;
  if (/carte/i.test(text)) return false;
  const words = cleaned.split(/\s+/);
  if (!words.some((word) => word.replace(/[^a-zA-ZÀ-ÿ]/g, "").length >= 3)) return false;
  const letterCount = (cleaned.match(/[a-zA-ZÀ-ÿ]/g) ?? []).length;
  const digitCount = (cleaned.match(/\d/g) ?? []).length;
  if (digitCount > 0 && letterCount / (letterCount + digitCount) < 0.5) return false;
  return true;
}

function isMeaningfulNameLine(text: string): boolean {
  return (
    !isForbiddenFieldLine(text) &&
    !isSexeLine(text) &&
    !isLikelyGenderValueLine(text) &&
    !/carte/i.test(text) &&
    cleanName(text).length > 2
  );
}

function assignNamePairFromLines(
  pair: Array<{ text: string; confidence: number }>,
  result: {
    lastname: string | null;
    firstname: string | null;
    lastnameConf: number;
    firstnameConf: number;
  }
): void {
  if (pair.length >= 2) {
    const [a, b] = pair;
    const cleanA = cleanName(a.text);
    const cleanB = cleanName(b.text);
    const aIsAllCaps = isAllCapsText(cleanA);
    const bIsAllCaps = isAllCapsText(cleanB);

    if (aIsAllCaps && !bIsAllCaps) {
      result.lastname = cleanA;
      result.lastnameConf = a.confidence;
      result.firstname = cleanB;
      result.firstnameConf = b.confidence;
    } else if (!aIsAllCaps && bIsAllCaps) {
      result.lastname = cleanB;
      result.lastnameConf = b.confidence;
      result.firstname = cleanA;
      result.firstnameConf = a.confidence;
    } else {
      result.lastname = cleanA;
      result.lastnameConf = a.confidence;
      result.firstname = cleanB;
      result.firstnameConf = b.confidence;
    }
  } else if (pair.length === 1) {
    const only = pair[0];
    const cleaned = cleanName(only.text);
    if (isAllCapsText(cleaned)) {
      result.lastname = cleaned;
      result.lastnameConf = only.confidence;
    } else {
      result.firstname = cleaned;
      result.firstnameConf = only.confidence;
    }
  }
}

function pickNameLinesBeforeAnchor(
  lines: Array<{ index: number; text: string; confidence: number }>,
  anchorIdx: number,
  preferLeadingPositions: boolean
): Array<{ text: string; confidence: number }> {
  const before = lines
    .filter((line) => line.index < anchorIdx && isMeaningfulNameLine(line.text))
    .sort((a, b) => a.index - b.index);

  if (preferLeadingPositions && anchorIdx >= 2) {
    const at0 = before.find((line) => line.index === 0);
    const at1 = before.find((line) => line.index === 1);
    if (at0 && at1) return [at0, at1];
  }

  if (before.length >= 2) return before.slice(-2);
  return before;
}

function extractFromFrame(lines: Array<{ index: number; text: string; confidence: number }>): {
  lastname: string | null;
  firstname: string | null;
  company: string | null;
  cardNumber: string | null;
  lastnameConf: number;
  firstnameConf: number;
  companyConf: number;
  cardNumberConf: number;
} {
  const sexeIdx = findSexeIndex(lines);
  const carteIdx = findCarteIndex(lines);
  const result = {
    lastname: null,
    firstname: null,
    company: null,
    cardNumber: null,
    lastnameConf: 0,
    firstnameConf: 0,
    companyConf: 0,
    cardNumberConf: 0,
  };

  for (const line of lines) {
    if (isForbiddenFieldLine(line.text)) continue;
    if (/carte/i.test(line.text) || /n[°o]\s*:/i.test(line.text)) {
      const num = extractCardNumberFromText(line.text);
      if (num) {
        result.cardNumber = num;
        result.cardNumberConf = line.confidence;
        break;
      }
    }
  }
  if (!result.cardNumber) {
    for (const line of lines) {
      if (isForbiddenFieldLine(line.text)) continue;
      const num = extractCardNumberFromText(line.text);
      if (num && /\d{7,}/.test(line.text.replace(/\s/g, ""))) {
        result.cardNumber = num;
        result.cardNumberConf = line.confidence;
        break;
      }
    }
  }

  const nameAnchorIdx = sexeIdx !== -1 ? sexeIdx : carteIdx;
  if (nameAnchorIdx === -1) return result;

  const useLeadingNames = sexeIdx === -1 && carteIdx !== -1;
  const nameLines = pickNameLinesBeforeAnchor(lines, nameAnchorIdx, useLeadingNames);
  assignNamePairFromLines(nameLines, result);

  const companyStartIdx = sexeIdx !== -1 ? sexeIdx : 1;
  const compWindow =
    carteIdx !== -1
      ? lines.filter((line) => line.index > companyStartIdx && line.index < carteIdx)
      : lines.filter((line) => line.index > companyStartIdx && !/carte/i.test(line.text));
  const compCandidates = compWindow.filter((line) => isCompanyLike(line.text));
  if (compCandidates.length > 0) {
    const best = [...compCandidates].sort((a, b) => b.confidence - a.confidence)[0];
    result.company = cleanName(best.text);
    result.companyConf = best.confidence;
  }

  return result;
}

function voteBest(candidates: Array<{ value: string; confidence: number }>): {
  value: string | null;
  confidence: number;
} {
  if (candidates.length === 0) return { value: null, confidence: 0 };
  const groups: Record<
    string,
    { values: string[]; confidences: number[]; original: string; bestConf: number }
  > = {};
  for (const candidate of candidates) {
    if (!candidate.value) continue;
    const key = candidate.value.toUpperCase().replace(/\s+/g, " ");
    if (!groups[key]) {
      groups[key] = { values: [], confidences: [], original: candidate.value, bestConf: 0 };
    }
    groups[key].values.push(candidate.value);
    groups[key].confidences.push(candidate.confidence || 1);
    if (candidate.confidence > groups[key].bestConf) {
      groups[key].original = candidate.value;
      groups[key].bestConf = candidate.confidence;
    }
  }
  let bestKey: string | null = null;
  let bestScore = -1;
  let bestAvgConf = 0;
  for (const [key, group] of Object.entries(groups)) {
    const freq = group.values.length;
    const avgConf = group.confidences.reduce((acc, value) => acc + value, 0) / group.confidences.length;
    const score = freq * (1 + avgConf / 100);
    if (score > bestScore) {
      bestScore = score;
      bestKey = key;
      bestAvgConf = avgConf;
    }
  }
  return bestKey
    ? { value: groups[bestKey].original, confidence: bestAvgConf }
    : { value: null, confidence: 0 };
}

function parseCardFrames(
  frames: Array<Array<{ index: number; text: string; confidence: number }>>
): {
  lastname: string | null;
  firstname: string | null;
  company: string;
  cardNumber: string | null;
  lastnameConf: number;
  firstnameConf: number;
  companyConf: number;
  cardNumberConf: number;
} {
  const lastnameCandidates: Array<{ value: string; confidence: number }> = [];
  const firstnameCandidates: Array<{ value: string; confidence: number }> = [];
  const companyCandidates: Array<{ value: string; confidence: number }> = [];
  const cardNumberCandidates: Array<{ value: string; confidence: number }> = [];

  for (const frame of frames) {
    const extracted = extractFromFrame(frame);
    if (extracted.lastname) {
      lastnameCandidates.push({ value: extracted.lastname, confidence: extracted.lastnameConf });
    }
    if (extracted.firstname) {
      firstnameCandidates.push({ value: extracted.firstname, confidence: extracted.firstnameConf });
    }
    if (extracted.company) {
      companyCandidates.push({ value: extracted.company, confidence: extracted.companyConf });
    }
    if (extracted.cardNumber) {
      cardNumberCandidates.push({
        value: extracted.cardNumber,
        confidence: extracted.cardNumberConf,
      });
    }
  }

  const lastnameVote = voteBest(lastnameCandidates);
  const firstnameVote = voteBest(firstnameCandidates);
  const companyVote = voteBest(companyCandidates);
  const cardNumberVote = voteBest(cardNumberCandidates);

  return {
    lastname: lastnameVote.value,
    firstname: firstnameVote.value,
    company: companyVote.value ?? "",
    cardNumber: cardNumberVote.value,
    lastnameConf: lastnameVote.confidence,
    firstnameConf: firstnameVote.confidence,
    companyConf: companyVote.confidence,
    cardNumberConf: cardNumberVote.confidence,
  };
}

function parsedToFieldConfidence(parsed: ReturnType<typeof parseCardFrames>): FieldConfidence {
  const confidence: FieldConfidence = {};
  if (parsed.lastname) confidence.lastname = parsed.lastnameConf;
  if (parsed.firstname) confidence.firstname = parsed.firstnameConf;
  if (parsed.company) confidence.company = parsed.companyConf;
  if (parsed.cardNumber) confidence.btp_number = parsed.cardNumberConf;
  return confidence;
}

/**
 * Parse OCR into fields and merged per-field confidence (multi-frame vote or single-frame lines).
 */
export function parseOcrFieldsWithConfidence(
  linesPerFrame?: OcrLine[][],
  mergedText?: string
): { fields: OcrFields; confidence: FieldConfidence } {
  const frames =
    linesPerFrame != null && linesPerFrame.length > 0
      ? linesPerFrame.map(linesToFrame)
      : mergedText
        ? [textToFrame(mergedText)]
        : [];

  if (frames.length === 0) {
    return { fields: {}, confidence: {} };
  }

  const parsed = parseCardFrames(frames);
  const fields: OcrFields = {
    lastname: parsed.lastname ?? undefined,
    firstname: parsed.firstname ?? undefined,
    company: parsed.company || undefined,
    btp_number: parsed.cardNumber ?? undefined,
  };
  return { fields, confidence: parsedToFieldConfidence(parsed) };
}

function textToFrame(text: string): Array<{ index: number; text: string; confidence: number }> {
  return text.split(/\n/).map((line, index) => ({ index, text: line.trim(), confidence: 0 }));
}

function linesToFrame(lines: OcrLine[]): Array<{ index: number; text: string; confidence: number }> {
  return lines.map((line, index) => ({ index, text: line.text.trim(), confidence: line.confidence }));
}

export interface MergeOcrOptions {
  /** Kept for compatibility with callers. */
  confidenceThreshold?: number;
}

export function mergeOcrTexts(
  texts: string[],
  linesPerFrame?: OcrLine[][],
  options?: MergeOcrOptions
): string {
  if (texts.length === 0) return "";
  void options;
  const lineArrays = texts.map((text) => text.split(/\n/).map((line) => line.trim()));
  const maxLines = Math.max(...lineArrays.map((arr) => arr.length));
  const merged: string[] = [];
  for (let i = 0; i < maxLines; i++) {
    const candidates = lineArrays.map((arr) => arr[i]).filter((line): line is string => !!line);
    if (candidates.length === 0) {
      merged.push("");
      continue;
    }
    const byText: Record<string, number> = {};
    for (const candidate of candidates) byText[candidate] = (byText[candidate] ?? 0) + 1;
    merged.push(Object.entries(byText).sort((a, b) => b[1] - a[1])[0]?.[0] ?? candidates[0]);
  }
  return merged.join("\n");
}

/**
 * Parse raw OCR text into structured fields using Sexe / Carte n° anchors and multi-frame voting.
 */
export function parseOcrTextFromRaw(text: string): OcrFields {
  return parseOcrFieldsWithConfidence(undefined, text).fields;
}

export function applySexeAnchorFallback(fields: OcrFields, linesPerFrame: OcrLine[][]): OcrFields {
  if (linesPerFrame == null || linesPerFrame.length === 0) return fields;
  const parsed = parseCardFrames(linesPerFrame.map(linesToFrame));
  return {
    ...fields,
    lastname: parsed.lastname ?? fields.lastname,
    firstname: parsed.firstname ?? fields.firstname,
    company: parsed.company || fields.company,
  };
}

export function applyNomPrenomFallback(fields: OcrFields, linesPerFrame: OcrLine[][]): OcrFields {
  if (linesPerFrame == null || linesPerFrame.length === 0) return fields;
  const parsed = parseCardFrames(linesPerFrame.map(linesToFrame));
  return {
    ...fields,
    lastname: parsed.lastname ?? fields.lastname,
    firstname: parsed.firstname ?? fields.firstname,
  };
}

export function applyCardNumberFallback(fields: OcrFields, linesPerFrame: OcrLine[][]): OcrFields {
  if (linesPerFrame == null || linesPerFrame.length === 0) return fields;
  const parsed = parseCardFrames(linesPerFrame.map(linesToFrame));
  return {
    ...fields,
    btp_number: parsed.cardNumber ?? fields.btp_number,
  };
}
